import { Divider, Switch, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  CurrencySwitcher,
  Link,
} from "../../../common/components";
import { Box, FlexColBox } from "../../../common/components/Box";
import Checkbox from "../../../common/components/Checkbox/Checkbox";
import { useSelector } from "../../../common/hooks/useSelector";
import { FormContext } from "../../../context";
import OrderService from "../../../service/OrderService";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { useCart } from "../../../utils/CartHooks";
import {
  canContinueToPayment,
  cartContainsDeprecatedDesign,
  cartContainsItemsThatExceedMax,
  getReducedCartItems,
} from "../../../utils/CartUtils";
import { ROUTES } from "../../../constant/common.constant";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { getLanguage } from "../../../utils/LocaleUtil";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import { AddressContainer } from "../../AddressContainer";
import { CheckoutForm } from "../Cart";
import NextStepInfo from "./components/NextStepInfo";
import { ProductLineType } from "../../../types";
import orderBackgroundEN from "../../../assets/images/order_next_steps_info_english.png";
import orderBackgroundFR from "../../../assets/images/order_next_steps_info_french.png";

import quoteBackgroundEN from "../../../assets/images/quote_next_steps_info_english.png";
import quoteBackgroundFR from "../../../assets/images/quote_next_steps_info_french.png";
import { CartComboItemsPreview } from "../CartComboItemsPreview";
import { useAuth } from "../../../common/hooks/useAuth";
import { CART_ACTION_ENUM } from "../../../common/reducers/cart";
import { useStore } from "react-redux";
import CartItemShipping from "../CartCheckout/components/CartItemShipping";
import CartCheckoutPaymentOptions from "../CartCheckout/components/CartCheckoutPaymentOptions";
import { useAppLoading } from "../../../context/AppLoading/useAppLoading";
import { toast } from "react-toastify";
import { productNameToProductLineAndFamily } from "../../../utils/product.utils";
import {
  trackCheckoutStarted,
  trackOrderCompleted,
} from "../../../utils/analytics.utils";
import CartCabonNeutral from "../CartCheckout/components/CartCabonNeutral";
import { useUserAddress } from "../../../common/hooks/useUserAddress";

const useStyles = (fullWidth: boolean) =>
  createStyles({
    container: {
      maxWidth: fullWidth ? "100%" : 400,
      display: "flex",
      flexDirection: "column",
      gap: { xs: spacing.mediumMobile, lg: spacing.groupingDesktop },
      pb: { xs: spacing.largeMobile, lg: 0 },
      background: { xs: palette.grey.lightBackground, lg: "unset" },
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
    },
    total: {
      display: "flex",
      flexDirection: "column",
      gap: { xs: spacing.mediumMobile, lg: spacing.mediumDesktop },
      mb: { xs: "8px", lg: 0 },
      px: { xs: spacing.wrapperMobile, lg: 0 },
    },
    items: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      px: { xs: spacing.wrapperMobile, lg: 0 },
      gap: { xs: spacing.mediumMobile, lg: spacing.mediumDesktop },
    },
  });

const CartPreview = () => {
  const { t, i18n } = useTranslation();
  const tEnglish = i18n.getFixedT("en");
  const { isMobile } = useThemeBreakpoints();
  const { dispatch } = useStore();
  const location = useLocation();
  const isShippingStep = location.pathname === ROUTES.CHECKOUT;
  const isMobileShippingStep = isMobile && isShippingStep;
  const styles = useStyles(isMobileShippingStep);
  const navigate = useNavigate();
  const userInfos = useSelector((state) => state.userInfo);
  const { formik } = useContext(FormContext);
  const { deleteOneTimeUsedAddresses } = useUserAddress();
  const {
    billingAddress,
    shippingAddress,
    clientDetails,
    finalDesignValidated,
    termsAndConditionsAccepted,
    poNumber,
    notes,
    paymentOption,
  } = formik?.values as CheckoutForm;
  const [submitLoading, setSubmitLoading] = useState(false);
  const { setIsAppLoading } = useAppLoading();
  const {
    cart,
    loaded,
    isSaving,
    showDistributorPrices,
    shippingFormStates,
    billingFormStates,
  } = useSelector((state) => state.cart);
  const { isDistributor, isAdmin } = useAuth();

  const { fetchCartItemsAndUpdateStore } = useCart();
  // const [promoCode, setPromoCode] = useState<string>("");

  const { NarrowCurrencyFormatter, getCurrency } = useCurrency();

  const cartContainsItemsNotAvailableInCountry = useMemo(() => {
    return cart.comboItems.some((comboItem) => {
      return comboItem.items.some((i) => {
        const notAvailableInUS =
          i.productName.toUpperCase().includes(ProductLineType.CLASSIC) &&
          (getCurrency() === "usd" || shippingAddress.country === "US");
        return notAvailableInUS;
      });
    });
  }, [cart, getCurrency, shippingAddress]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cartContainsProductVersionNeedUpgrade = useMemo(() => {
    return cart.comboItems.some((comboItem) => {
      return comboItem.items.some((i) => i.productVersionNeedUpgrade);
    });
  }, [cart.comboItems]);

  const areAllQuantitiesAvailable = useMemo(
    () => cart.pricingRestrictionsAreMet,
    [cart.pricingRestrictionsAreMet]
  );

  //Can't use memo because shippingAddress object doesn't change, it's their properties that are changing
  const isFormValid = () => {
    return formik?.isValid;
  };

  const onShowDistributorPricesChange = useCallback(
    (checked: boolean) => {
      dispatch({
        type: CART_ACTION_ENUM.SET_SHOW_DISTRIBUTOR_PRICES,
        payload: { showDistributorPrices: checked },
      });
    },
    [dispatch]
  );

  const onContinueToShippingClick = useCallback(async () => {
    const { shippingTotal, orderTotal } = {
      shippingTotal:
        showDistributorPrices && !!cart.distributor
          ? cart.distributor?.shippingTotal
          : cart.shippingTotal,
      orderTotal:
        showDistributorPrices && !!cart.distributor
          ? cart.distributor?.orderTotal
          : cart.orderTotal,
    };
    trackCheckoutStarted({
      order_id: userInfos.id,
      value: orderTotal,
      revenue: orderTotal - shippingTotal,
      shipping: shippingTotal,
      currency: cart.currency,
      products: getReducedCartItems(cart?.comboItems).map((itemPricing) => {
        const { family, productLine } = productNameToProductLineAndFamily(
          itemPricing.productName
        );
        const pricePerUnit =
          showDistributorPrices && !!itemPricing.distributor
            ? itemPricing.distributor?.pricePerUnit
            : itemPricing.pricePerUnit;

        return {
          product_id: itemPricing.productName,
          sku: itemPricing.designId,
          name: itemPricing.productName,
          category: family?.labelTag ? tEnglish(family?.labelTag) : "",
          brand: tEnglish(`line.${productLine}.title`),
          price: pricePerUnit,
          quantity: itemPricing.quantity,
        };
      }),
    });
    navigate(ROUTES.CHECKOUT);
  }, [
    cart?.comboItems,
    cart.currency,
    cart.distributor,
    cart.orderTotal,
    cart.shippingTotal,
    navigate,
    showDistributorPrices,
    tEnglish,
    userInfos.id,
  ]);

  const onPreOrder = useCallback(async () => {
    setSubmitLoading(true);
    setIsAppLoading(true);
    const itemsToPreOrder = getReducedCartItems(cart.comboItems).map((item) => {
      return { designId: item.designId, sizes: item.sizes };
    });

    const preOrderData = {
      shippingAddress: shippingAddress,
      billingAddress:
        billingAddress.id === "default-shipping"
          ? shippingAddress
          : billingAddress,
      items: itemsToPreOrder,
      locale: getLanguage(),
      currency: getCurrency(),
      clientDetails: clientDetails,
      promoCode: "",
      showDistributorPrices,
      poNumber,
      notes,
      paymentOption,
    };

    try {
      const checkoutRes = await OrderService.addOrder(preOrderData);

      //TODO: Error message to user when failing
      if (checkoutRes.status !== 200) {
        console.error("Something somewhere went wrong!", checkoutRes);
        return;
      }

      const orderId = checkoutRes.data.data.id;

      deleteOneTimeUsedAddresses();
      fetchCartItemsAndUpdateStore();
      navigate(
        generatePath(ROUTES.ORDERS_SUCCESS, {
          orderId: orderId,
        })
      );
      setSubmitLoading(false);
      setIsAppLoading(false);
      const { shippingTotal, orderTotal } = {
        shippingTotal:
          showDistributorPrices && !!cart.distributor
            ? cart.distributor?.shippingTotal
            : cart.shippingTotal,
        orderTotal:
          showDistributorPrices && !!cart.distributor
            ? cart.distributor?.orderTotal
            : cart.orderTotal,
      };
      trackOrderCompleted({
        checkout_id: orderId,
        order_id: userInfos.id,
        subtotal: orderTotal - shippingTotal,
        total: orderTotal,
        revenue: orderTotal - shippingTotal,
        shipping: shippingTotal,
        tax: 0,
        currency: cart.currency,
        products: getReducedCartItems(cart?.comboItems).map((itemPricing) => {
          const { family, productLine } = productNameToProductLineAndFamily(
            itemPricing.productName
          );
          const pricePerUnit =
            showDistributorPrices && !!itemPricing.distributor
              ? itemPricing.distributor?.pricePerUnit
              : itemPricing.pricePerUnit;
          return {
            product_id: itemPricing.productName,
            sku: itemPricing.designId,
            name: itemPricing.productName,
            category: family?.labelTag ? tEnglish(family?.labelTag) : "",
            brand: tEnglish(`line.${productLine}.title`),
            price: pricePerUnit,
            quantity: itemPricing.quantity,
          };
        }),
      });
    } catch (error) {
      console.error("Something somewhere went wrong!", error);
      toast.error(t("errors.toast.request_failed"), {
        toastId: "request_failed",
      });
      setSubmitLoading(false);
      return;
    }
  }, [
    setIsAppLoading,
    cart.comboItems,
    cart.distributor,
    cart.shippingTotal,
    cart.orderTotal,
    cart.currency,
    shippingAddress,
    billingAddress,
    getCurrency,
    clientDetails,
    showDistributorPrices,
    poNumber,
    notes,
    paymentOption,
    deleteOneTimeUsedAddresses,
    fetchCartItemsAndUpdateStore,
    navigate,
    userInfos.id,
    tEnglish,
    t,
  ]);

  const containsDeprecatedDesign = cartContainsDeprecatedDesign(
    cart.comboItems
  );

  const isValidAddressesButNotSubmitted = useMemo(() => {
    return (
      shippingFormStates.isFormOpenedAndValid ||
      billingFormStates.isFormOpenedAndValid
    );
  }, [shippingFormStates, billingFormStates]);

  //preload NextStepInfo images
  useEffect(() => {
    const orderBackgroundENTemp = new Image();
    orderBackgroundENTemp.src = orderBackgroundEN;
    const orderBackgroundFRTemp = new Image();
    orderBackgroundFRTemp.src = orderBackgroundFR;
    const quoteBackgroundENTemp = new Image();
    quoteBackgroundENTemp.src = quoteBackgroundEN;
    const quoteBackgroundFRTemp = new Image();
    quoteBackgroundFRTemp.src = quoteBackgroundFR;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { shippingTotal, orderTotal, carbonNeutralityFeeTotal } = {
    shippingTotal: showDistributorPrices
      ? cart.distributor?.shippingTotal
      : cart.shippingTotal,
    orderTotal: showDistributorPrices
      ? cart.distributor?.orderTotal
      : cart.orderTotal,
    carbonNeutralityFeeTotal: showDistributorPrices
      ? cart.distributor?.carbonNeutralityFeeTotal
      : cart.carbonNeutralityFeeTotal,
  };

  const showDistributorSwitch = isDistributor || isAdmin;

  return (
    <Box key="preview" sx={styles.container}>
      <FlexColBox
        gap={{ xs: spacing.mediumMobile, lg: spacing.groupingDesktop }}
        sx={{
          background: { xs: palette.white, lg: "unset" },
        }}
      >
        {showDistributorSwitch && (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={"4px"}
            px={{ xs: spacing.wrapperMobile, lg: 0 }}
            mt={{ xs: spacing.wrapperMobile, lg: 0 }}
          >
            <Typography
              variant="textSm"
              component="label"
              htmlFor="showDistributorPrices"
              sx={{
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {t("pricing.viewDistributorPrices")}
            </Typography>
            <Switch
              id="showDistributorPrices"
              checked={showDistributorPrices}
              onChange={(_, checked) => onShowDistributorPricesChange(checked)}
            />
          </Box>
        )}
        {isMobileShippingStep && (
          <Typography
            variant="h1"
            mt={showDistributorSwitch ? 0 : spacing.wrapperMobile}
            px={spacing.wrapperMobile}
          >
            {t(
              areAllQuantitiesAvailable
                ? "checkout.checkout"
                : "checkout.requestQuote"
            )}
          </Typography>
        )}
        <Box sx={styles.items}>
          {cart.comboItems.map((comboItem, index) => (
            <CartComboItemsPreview
              comboItems={comboItem}
              key={`${comboItem.familyName}-${index}`}
            />
          ))}
        </Box>

        <Divider />

        <Box sx={styles.total}>
          <Box sx={styles.row}>
            <Typography variant="textRegular">
              {t("checkout.shipping")}
            </Typography>
            <Typography variant="textRegular">
              {!containsDeprecatedDesign && cart.pricingRestrictionsAreMet
                ? NarrowCurrencyFormatter?.format(shippingTotal || 0)
                : t("checkout.tbd")}
            </Typography>
          </Box>
          {/* <Box sx={styles.row}>
            <Typography variant="textRegular">
              {t("checkout.carbon_offset")}
            </Typography>
            <Typography variant="textRegular">
              {!containsDeprecatedDesign && cart.pricingRestrictionsAreMet
                ? NarrowCurrencyFormatter?.format(
                    cart.carbonNeutral ? carbonNeutralityFeeTotal || 0 : 0
                  )
                : t("checkout.tbd")}
            </Typography>
          </Box> */}
          <Box sx={styles.row}>
            <Typography variant="textRegular">{t("checkout.taxes")}</Typography>
            <Typography variant="textRegular">{t("checkout.tbd")}</Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={{ xs: spacing.xsMobile, lg: spacing.xsDesktop }}
          >
            <Box sx={styles.row}>
              <Typography variant="textRegularBold">
                {t("checkout.subtotal", { currency: getCurrency() })}
              </Typography>
              <Typography variant="textRegularBold">
                {!containsDeprecatedDesign && cart.pricingRestrictionsAreMet
                  ? NarrowCurrencyFormatter?.format(orderTotal || 0)
                  : t("checkout.tbd")}
              </Typography>
            </Box>
            <Box>
              <CurrencySwitcher />
            </Box>
          </Box>
        </Box>
      </FlexColBox>
      {/* {isShippingStep && (
        <Box>
          <Input
            label={t("checkout.promoCode")}
            name="promoCode"
            value={promoCode}
            onChange={(e) => setPromoCode(e.currentTarget.value)}
            fullWidth
          />
        </Box>
      )} */}
      {isMobileShippingStep && (
        <>
          <Box
            sx={{
              background: palette.grey.lightBackground,
            }}
          >
            <AddressContainer />
          </Box>
          <Box>
            <CartItemShipping />
          </Box>
          <CartCheckoutPaymentOptions />
          <FlexColBox
            gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
            sx={{
              background: palette.white,
              py: `${spacing.mediumMobile}`,
              px: spacing.wrapperMobile,
            }}
          >
            <Typography variant="h2">
              {t("checkout.additional_options")}
            </Typography>
            <CartCabonNeutral />
            <Input
              name="poNumber"
              data-test="checkout.poNumber"
              value={poNumber}
              onChange={(e: any) =>
                formik?.setFieldValue("poNumber", e.target.value)
              }
              label={t("checkout.po_number")}
              autoComplete="off"
              fullWidth
            />
            <Input
              name="notes"
              data-test="checkout.notes"
              value={notes}
              onChange={(e: any) =>
                formik?.setFieldValue("notes", e.target.value)
              }
              label={t("checkout.notes")}
              autoComplete="off"
              fullWidth
            />
          </FlexColBox>
        </>
      )}
      {isShippingStep && (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
          px={{ xs: spacing.wrapperMobile, lg: 0 }}
          pt={{ xs: spacing.regularMobile, lg: 0 }}
        >
          <NextStepInfo
            hasReachedMaximum={
              !!cartContainsItemsThatExceedMax(cart.comboItems)
            }
          />
          <Checkbox
            onChange={async (_, value) => {
              await formik?.setFieldValue("finalDesignValidated", value, true);
              await formik?.setFieldValue(
                "termsAndConditionsAccepted",
                value,
                true
              );
            }}
            data-test="cart.termsAndConditions"
            label={t("checkout.termsAndConditions.text")}
            linkUrl={t("checkout.termsAndConditions.linkUrl")}
            textUrl={t("checkout.termsAndConditions.textUrl")}
            value={finalDesignValidated && termsAndConditionsAccepted}
          />
        </Box>
      )}
      {!isShippingStep ? (
        <FlexColBox
          sx={{
            px: { xs: spacing.wrapperMobile, lg: 0 },
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Button
            disabled={
              !canContinueToPayment(
                isSaving,
                cart.comboItems,
                cart,
                userInfos
              ) ||
              cartContainsItemsNotAvailableInCountry ||
              containsDeprecatedDesign
            }
            onClick={onContinueToShippingClick}
            fullWidth
            data-test="cart.continue"
          >
            {isSaving ? t("checkout.calculating") : t("general.continue")}
          </Button>
          <Box maxWidth={"fit-content"}>
            <Link
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cart.add_more_products")}
            </Link>
          </Box>
        </FlexColBox>
      ) : (
        <Box
          sx={{
            px: { xs: spacing.wrapperMobile, lg: 0 },
          }}
        >
          <Button
            disabled={
              !isFormValid() ||
              !canContinueToPayment(
                isSaving,
                cart.comboItems,
                cart,
                userInfos
              ) ||
              !loaded ||
              submitLoading ||
              cartContainsItemsNotAvailableInCountry ||
              containsDeprecatedDesign
            }
            onClick={onPreOrder}
            fullWidth
            data-test="cart.submit"
            helperText={
              isValidAddressesButNotSubmitted
                ? t("checkout.helperTexts.address_not_submitted", {
                    actionText:
                      shippingFormStates.hasCheckedSaveAndContinue ||
                      billingFormStates.hasCheckedSaveAndContinue
                        ? t("general.saveAndContinue")
                        : t("general.continue"),
                  })
                : undefined
            }
          >
            {areAllQuantitiesAvailable
              ? t("checkout.submitOrder")
              : t("checkout.requestQuoteBtn")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CartPreview;
